'use client';

import { useRef, useEffect } from 'react';
import type { JobPosition } from '../types';
import JobCard from './JobCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Mousewheel } from 'swiper/modules';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Swiperのスタイルをインポート
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/mousewheel';

interface JobListProps {
  positions: JobPosition[];
  activeIndex?: number;
  onSlideChange?: (index: number) => void;
}

export function JobList({
  positions,
  activeIndex = 0,
  onSlideChange
}: JobListProps) {
  const swiperRef = useRef<any>(null);
  const tailwindBreakpointsMd = 768;
  const isDesktop = useMediaQuery(`(min-width: ${tailwindBreakpointsMd}px)`);

  // activeIndexが変更された場合、スライドを移動
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    // スライダーを初期位置（1番目）に戻す
    swiper.slideToLoop(activeIndex);
  }, [swiperRef, activeIndex, positions]); // positionsが変わったときも実行

  return (
    <div className="mx-auto max-w-[368px] md:max-w-[880px]">
      <Swiper
        ref={swiperRef}
        modules={[Navigation, Pagination, A11y, Mousewheel]}
        // centeredSlides={true} // スライドを中央に配置 // これを有効にすると、ページネーションが崩れるため一時的にコメントアウト
        spaceBetween={isDesktop ? 20 : 12} // スライド間のスペース
        slidesPerView={isDesktop ? 4 : 2} // 一度に表示するスライド数
        slidesPerGroup={isDesktop ? 4 : 2} // 一度にスライドを切り替える数
        loop={true} // ループ再生
        initialSlide={0} // 初期スライドを1番目に設定
        key={positions[0]?.id || 'empty'} // positionsが変わったら新しいSwiperインスタンスを作成
        className="job-swiper"
        pagination={{
          clickable: true,
          type: 'bullets', // ドット型のページネーション
          bulletClass: 'swiper-pagination-bullet job-pagination-bullet',
          bulletActiveClass:
            'swiper-pagination-bullet-active job-pagination-bullet-active',
          el: '.swiper-pagination' // ページネーションコンテナを指定
        }}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 0.5,
          thresholdDelta: 50
        }}
        direction="horizontal"
        allowTouchMove={true}
        onSlideChange={swiper => {
          if (onSlideChange) {
            onSlideChange(swiper.realIndex);
          }
        }}
      >
        {positions.map(position => (
          <SwiperSlide key={position.id}>
            <JobCard {...position} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="mt-4 flex items-center justify-center">
        <div className="flex items-center gap-4">
          <div className="swiper-button-prev !static !m-0 flex items-center justify-center"></div>
          <div className="swiper-pagination !static flex items-center justify-center gap-4"></div>
          <div className="swiper-button-next !static !m-0 flex items-center justify-center"></div>
        </div>
      </div>
    </div>
  );
}
